import { useEffect, useRef, useState } from "react";
import type { CafeChargeConfig, CafeChargeHotspot } from "../scenario/types";
import type { PinScreen } from "../three/PackPinProjector";
import { audio } from "../lib/audio";
import { ChargeExfil } from "./ChargeExfil";
import { BadUsbExplain } from "./BadUsbExplain";
import { tx, txr } from "../lib/tx";

export type ChargeResult = {
  safe: boolean;
  eventId: string;
  outcome: string;
  label: string;
};

/**
 * Cafe charging as CLICKABLE in-room objects. Pins are projected onto the live
 * Marble pano (reusing PackPinProjector), so the learner looks around the cafe
 * and taps a real baked object.
 *
 * Staged so the teaching moment lands BEFORE the safe choice, not after a free
 * pick among all three: stage 1 shows ONLY the trap (public USB) — the learner
 * must plug in and see the BadUSB compromise play out. Stage 2, after the
 * teach, reveals the two safe options (own charger / wall outlet) and explains
 * why each is safe.
 */
export function CafeCharge({
  config,
  pinScreens,
  onRiskEvent,
  onComplete,
  onStageChange,
}: {
  config: CafeChargeConfig;
  pinScreens: PinScreen[];
  /** Fired once the BadUSB trap has been picked and the exfil cinematic is
   *  dismissed — records the risk event WITHOUT ending the beat. */
  onRiskEvent: (result: ChargeResult) => void;
  onComplete: (result: ChargeResult) => void;
  /** Lets App.tsx swap the top-right guide tooltip copy per stage (mirrors
   *  the publicpc/byodlaptop pattern) instead of duplicating the same
   *  headline/body in this component's own aside panel. */
  onStageChange?: (stage: "risky" | "safe") => void;
}) {
  const allItems = config.hotspots ?? [];
  const [stage, setStage] = useState<"risky" | "safe">("risky");
  const items = allItems.filter((h) => (stage === "risky" ? !h.safe : !!h.safe));

  useEffect(() => {
    onStageChange?.(stage);
  }, [stage, onStageChange]);

  const [openId, setOpenId] = useState<string | null>(null);
  const [exfil, setExfil] = useState<ChargeResult | null>(null);
  // 2026-07-14: "What just happened?" used to skip straight to the safe-
  // charging stage with no real explanation. Now it opens a dedicated
  // BadUSB/juice-jacking explainer (with illustration) and only THAT
  // screen's own Continue button advances the beat.
  const [explaining, setExplaining] = useState<ChargeResult | null>(null);
  const [safeResult, setSafeResult] = useState<ChargeResult | null>(null);
  const spoke = useRef(false);

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    void audio.prefetchSpeech(
      config.safeBody ?? "Now charge Max's phone the safe way instead.",
      { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" },
    );
    if (config.lenaSafe) void audio.prefetchSpeech(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
    for (const h of allItems) {
      if (h.pickLine) void audio.prefetchSpeech(h.pickLine, { voice: "nova", lang: config.language ?? "en-US" });
    }
    if (spoke.current) return;
    spoke.current = true;
    void audio.speak(
      config.body ?? "Phone at four percent. A public USB port is right there on the wall.",
      { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" },
    );
    return () => audio.cancelSpeech();
  }, [config]);

  const byId = new Map(pinScreens.map((p) => [p.id, p]));
  const open = items.find((i) => i.id === openId) ?? null;

  if (exfil) {
    return (
      <ChargeExfil
        caption={config.goblinCaption}
        line={config.lenaRisky}
        onDone={() => {
          // Don't advance yet -- hand off to the real explainer first.
          setExplaining(exfil);
          setExfil(null);
        }}
      />
    );
  }

  if (explaining) {
    return (
      <BadUsbExplain
        onContinue={() => {
          onRiskEvent(explaining);
          setExplaining(null);
          setOpenId(null);
          setStage("safe");
          void audio.speak(
            config.safeBody ?? "Now charge Max's phone the safe way instead.",
            { voice: config.voiceId ?? "onyx", lang: config.language ?? "en-US" },
          );
        }}
      />
    );
  }

  if (safeResult) {
    return (
      <div className="rw-panel cafe-safe-feedback">
        <div className="rw-kicker">{tx("Safe charging choice")}</div>
        <h2>{tx("Own power. No data path.")}</h2>
        <p className="rw-body">
          {txr("{label} keeps Max's phone away from unknown USB hardware. A wall AC outlet or his own charger supplies power without trusting a stranger's data connection.", { label: safeResult.label })}
        </p>
        <button type="button" className="btn" onClick={() => onComplete(safeResult)}>
          {tx("Continue")}
        </button>
      </div>
    );
  }

  const choose = (h: CafeChargeHotspot) => {
    const result: ChargeResult = {
      safe: !!h.safe,
      eventId: h.eventId ?? (h.safe ? "charger_safe" : "charger_bad"),
      outcome: h.outcome ?? (h.safe ? "safe" : "risky"),
      label: h.label,
    };
    // Trap: SHOW the compromise (attacker-side exfil cinematic) before the teach.
    if (h.goblin) {
      setExfil(result);
      return;
    }
    if (h.pickLine) void audio.speak(h.pickLine, { voice: "nova", lang: config.language ?? "en-US" });
    else if (h.safe && config.lenaSafe) void audio.speak(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
    setSafeResult(result);
  };

  return (
    <div className="pack-room pack-room--pano cafecharge" aria-label={tx("Charge Max's phone")}>
      <div className="pack-room-pins-layer">
        {items.map((h) => {
          const scr = byId.get(h.id);
          if (!scr?.visible) return null;
          const isTrap = !h.safe;
          return (
            <button
              key={h.id}
              type="button"
              className={`hotspot-pin pack-pin ${openId === h.id ? "on" : ""} ${isTrap ? "trap" : ""} ${isTrap && stage === "risky" ? "cafecharge-only-option" : ""} ${h.spriteUrl ? "has-sprite" : ""}`}
              style={{ left: `${scr.xPct}%`, top: `${scr.yPct}%` }}
              onClick={() => setOpenId(h.id === openId ? null : h.id)}
              title={h.label}
            >
              {h.spriteUrl && (
                <img
                  className="pack-sprite"
                  src={h.spriteUrl}
                  alt=""
                  aria-hidden
                  style={{
                    width: `${h.spriteWidthPct ?? 8}vw`,
                    transform:
                      h.spriteAnchor === "center"
                        ? "translate(-50%, -50%)"
                        : "translate(-50%, -100%)",
                  }}
                />
              )}
              <span className="hotspot-dot" aria-hidden />
              <span className="hotspot-label">{h.label}</span>
            </button>
          );
        })}
      </div>

      <div className="pack-room-hint" aria-hidden>
        {stage === "risky"
          ? tx("Drag to look around · Tap the public USB port — it's the only way to charge here")
          : tx("Drag to look around · Tap a safe way to charge")}
      </div>

      {open && (
        <div className="hotspot-panel pack-room-panel cafecharge-panel">
          <strong>{open.label}</strong>
          {open.detail && <p>{open.detail}</p>}
          {!open.safe && (
            <p className="cafecharge-forced-note">
              {tx("There's no safer option on this wall right now — go ahead and charge with it to see what happens next.")}
            </p>
          )}
          <div className="cafecharge-actions">
            <button
              type="button"
              className={`btn ${open.safe ? "" : "btn-danger cafecharge-cta-glow"}`}
              onClick={() => choose(open)}
            >
              {tx("Charge with this")}
            </button>
          </div>
        </div>
      )}

      {/* 2026-07-15: the headline + body + forced-note used to repeat here
          AND in the top-right guide tooltip (App.tsx's CAFECHARGE_STAGE_GUIDE)
          -- same corner, tooltip drawn on top, so the tail of this note's
          text peeked out from underneath it. The guide tooltip is now the
          single place that copy lives; this aside stays only as the
          darkened backdrop panel + a short kicker so the two never compete
          for the same lines again. */}
      <aside className="pack-bag cafecharge-aside" aria-label={tx("Charging options")}>
        <div className="pack-bag-head">
          <div className="rw-kicker">{tx("Cafe · low battery")}</div>
        </div>
      </aside>
    </div>
  );
}
